import { useFocusEffect } from "expo-router";
import { useCallback, useRef, useState } from "react";

import { contextReviewStorageKey, type ContextAnswerReview } from "@/lib/context-hint-review";
import { getLocalFlag, setLocalFlag } from "@/lib/local-flags";

/** A local review gate. Closing the screen cancels completion, never implicitly dismisses it. */
export function useContextAnswerReview(scope: string, items: ContextAnswerReview[]) {
    const latest = useRef(items);
    latest.current = items;
    const [pending, setPending] = useState<ContextAnswerReview[] | null>(null);
    const [saving, setSaving] = useState(false);
    const acknowledged = useRef(new Set<string>());
    const resolveReview = useRef<((proceed: boolean) => void) | null>(null);
    const generation = useRef(0);
    const running = useRef(false);
    const savingRef = useRef(false);
    useFocusEffect(
        useCallback(() => {
            acknowledged.current = new Set();
            setPending(null);
            return () => {
                generation.current++;
                resolveReview.current?.(false);
                resolveReview.current = null;
            };
        }, [scope])
    );

    const review = useCallback(async (): Promise<boolean> => {
        if (running.current) {
            return false;
        }
        running.current = true;
        const version = generation.current;
        try {
            while (version === generation.current) {
                const candidates = latest.current;
                const flags = await Promise.all(
                    candidates.map(async (item) => {
                        const key = contextReviewStorageKey(scope, item);
                        if (acknowledged.current.has(key)) {
                            return true;
                        }
                        return (await getLocalFlag(key).catch(() => null)) === "true";
                    })
                );
                if (version !== generation.current) {
                    return false;
                }
                if (candidates !== latest.current) {
                    continue;
                }
                const remaining = candidates.filter(
                    (item, i) =>
                        !flags[i] && latest.current.some((current) => current.key === item.key)
                );
                if (!remaining.length) {
                    return true;
                }
                const proceed = await new Promise<boolean>((resolve) => {
                    resolveReview.current = resolve;
                    setPending(remaining);
                });
                if (!proceed) {
                    return false;
                }
                // Answers may have changed while reviewing. Check for new,
                // undismissed suggestions before continuing to validation.
            }
            return false;
        } finally {
            running.current = false;
        }
    }, [scope]);

    const dismiss = useCallback(
        async (itemKey?: string) => {
            if (!pending || savingRef.current) {
                return;
            }
            savingRef.current = true;
            setSaving(true);
            const version = generation.current;
            try {
                await Promise.all(
                    pending
                        .filter(
                            (item) =>
                                (itemKey === undefined || item.key === itemKey) &&
                                latest.current.some((current) => current.key === item.key)
                        )
                        .map(async (item) => {
                            const key = contextReviewStorageKey(scope, item);
                            acknowledged.current.add(key);
                            await setLocalFlag(key, "true").catch(() => {});
                        })
                );
                if (version === generation.current) {
                    const remaining = pending.filter(
                        (item) =>
                            latest.current.some((current) => current.key === item.key) &&
                            !acknowledged.current.has(contextReviewStorageKey(scope, item))
                    );
                    if (remaining.length) {
                        setPending(remaining);
                    } else {
                        setPending(null);
                        resolveReview.current?.(true);
                        resolveReview.current = null;
                    }
                }
            } finally {
                savingRef.current = false;
                setSaving(false);
            }
        },
        [pending, scope]
    );

    return {
        review,
        dismiss,
        pending:
            pending?.filter((item) => items.some((current) => current.key === item.key)) ?? null,
        saving
    };
}
